<template>
    <MainContent
        title="Recogida de indumentarias"
        :value="filters"
        :meta="meta"
        @input="val => Object.assign(filters, val)"
        @updated="fetchRecogida"
    >
        <template #header>
            <Card customClasses="tw:mb-4">
                <SearchComponent
                    :value="filters.search"
                    placeholder="Buscar..."
                    @input="val => filters.search = val"
                />
            </Card>
        </template>

        <!-- Skeleton: espeja la Card por deportista píxel a píxel -->
        <template v-if="loading">
            <Card
                v-for="n in 3"
                :key="'sk-dep-' + n"
                customClasses="tw:mb-4"
            >
                <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                    <template #skeleton>
                        <!-- Cabecera: mismo flex col→row con gap-3 -->
                        <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:sm:items-center tw:justify-between tw:gap-3">
                            <div class="tw:flex tw:flex-col tw:items-start">
                                <!-- h3: text-lg (28px) -->
                                <SkeletonItem height="tw:h-7" customClass="tw:w-56" />
                                <!-- p DNI: text-sm (20px) + mt-1 -->
                                <SkeletonItem height="tw:h-5" customClass="tw:w-72 tw:mt-1" />
                                <!-- Badge: text-xs uppercase (16px) + mt-2 -->
                                <SkeletonItem height="tw:h-4" customClass="tw:w-32 tw:mt-2" />
                            </div>
                            <!-- Botón "Marcar todas": text-sm + px-3 py-1 (28px) -->
                            <SkeletonItem height="tw:h-7" customClass="tw:w-44" />
                        </div>

                        <!-- Separador hr: my-4 = 16px cada lado -->
                        <div class="tw:my-4 tw:h-px tw:bg-gray-200 tw:w-full"></div>

                        <!-- Tabla skeleton via BaseTable (animate-pulse, fidelidad píxel con la real) -->
                        <BaseTable
                            :columns="indumentariaFields"
                            :skeleton="true"
                            :skeleton-rows="4"
                            naked
                        >
                            <template #skeleton-recogida>
                                <div class="tw:h-5 tw:w-9 tw:bg-gray-200 tw:rounded-full"></div>
                            </template>
                        </BaseTable>
                    </template>
                </Skeleton>
            </Card>
        </template>

        <!-- Empty state -->
        <div
            v-else-if="deportistas.length === 0"
            class="tw:bg-blue-50 tw:border tw:border-blue-200 tw:rounded tw:p-4 tw:text-blue-800"
            role="alert"
        >
            No se encontraron deportistas con indumentarias asignadas.
        </div>

        <!-- Deportista cards -->
        <template v-else>
            <Card
                v-for="deportista in deportistas"
                :key="deportista.id"
                customClasses="tw:mb-4"
            >
                <!-- Deportista header -->
                <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:sm:items-center tw:justify-between tw:gap-3">
                    <div>
                        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:m-0">
                            {{ deportista.nombre }} {{ deportista.apellidos }}
                        </h3>
                        <p class="tw:text-sm tw:text-gray-500 tw:mt-1 tw:mb-0">
                            DNI: {{ deportista.dni }}
                            <span v-if="deportista.equipo"> · {{ deportista.equipo.nombre }}</span>
                            <span v-if="deportista.categoria"> · {{ deportista.categoria.nombre }}</span>
                        </p>
                        <Badge
                            :variant="todasRecogidas(deportista) ? 'success' : 'warning'"
                            customClass="tw:mt-2"
                        >
                            {{ contarRecogidas(deportista) }}/{{ deportista.indumentarias.length }} prendas recogidas
                        </Badge>
                    </div>

                    <Button
                        :disabled="todasRecogidas(deportista) || marcandoGlobal === deportista.id"
                        :loading="marcandoGlobal === deportista.id"
                        customClass="tw:bg-green-500 tw:text-white tw:px-3 tw:py-1 tw:text-sm tw:rounded tw:hover:bg-green-700 tw:flex tw:items-center tw:gap-2"
                        ignoreBaseClasses
                        @click="handleMarcarTodas(deportista)"
                    >
                        Marcar todas como recogidas
                    </Button>
                </div>

                <hr class="tw:my-4 tw:border-gray-200" />

                <!-- Indumentarias table -->
                <BaseTable
                    :columns="indumentariaFields"
                    :data="deportista.indumentarias"
                    naked
                    :clickable-rows="true"
                    empty-text="Sin prendas asignadas"
                    @row-click="({ row }) => handleRowClick(deportista, row)"
                >
                    <template #cell-recogida="{ value, row }">
                        <span @click.stop>
                            <ToggleSwitch
                                :value="value"
                                variant="success"
                                size="sm"
                                wrapperClass="tw:justify-center"
                                :disabled="row._saving"
                                @input="(v) => handleToggleRecogida(deportista, row, v)"
                            />
                        </span>
                    </template>
                </BaseTable>
            </Card>
        </template>
    </MainContent>
</template>

<script setup>
    import { onMounted } from 'vue'

    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import Badge from '@/modules/admin/ui/Badge.vue'
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue'
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue'
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'

    import { useRecogidaIndumentarias } from '@/composables/admin/bases/useRecogidaIndumentarias.js'
    import { useToast } from '@/composables/common/useToast'

    const toast = useToast()

    const {
        loading,
        filters,
        meta,
        deportistas,
        indumentariaFields,
        marcandoGlobal,
        fetchRecogida,
        marcarTodas,
        toggleRecogida,
        contarRecogidas,
        todasRecogidas
    } = useRecogidaIndumentarias()

    // ==================== EVENT HANDLERS ====================
    async function handleMarcarTodas(deportista) {
        try {
            const minLock = new Promise(resolve => setTimeout(resolve, 1000))
            const res = await marcarTodas(deportista)
            await minLock
            toast.success(res.data.message)
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al marcar')
        }
    }

    async function handleToggleRecogida(deportista, indumentaria, value) {
        try {
            const minLock = new Promise(resolve => setTimeout(resolve, 1000))
            await toggleRecogida(deportista, indumentaria, value)
            await minLock
            toast.success(indumentaria.nombre + (value ? ' recogida' : ' pendiente'))
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al actualizar')
        }
    }

    function handleRowClick(deportista, indumentaria) {
        if (indumentaria._saving) return
        handleToggleRecogida(deportista, indumentaria, !indumentaria.recogida)
    }

    // ==================== LIFECYCLE ====================
    onMounted(() => {
        fetchRecogida()
    })
</script>
